import React, { useMemo } from 'react';
import type { SigningField } from '../types/fields';
import {
  fieldTextLayoutMode,
  getTextBaselineFromTop,
  alignedLineX,
  type FieldTextLayoutMode,
} from './textLayout';
import { resolveFieldTextStyle, toPreviewCss, type PreviewTextCss } from './fontMapping';
import { wrapTextToWidth } from './textFieldMeasure';

// Module-scope scratch canvas + 2D context, lazy-initialised on first use.
// Reusing a single canvas across every field×line×render keeps GC pressure
// flat in drag/resize loops (previously: ~200 fields × 5 lines × 60fps = 60k
// canvas allocations per second during a drag with letter-spaced text).
let _scratchCanvas: HTMLCanvasElement | null = null;
let _scratchCtx: CanvasRenderingContext2D | null = null;
function getScratchCtx(): CanvasRenderingContext2D | null {
  if (_scratchCtx) return _scratchCtx;
  if (typeof document === 'undefined') return null;
  _scratchCanvas = document.createElement('canvas');
  _scratchCtx = _scratchCanvas.getContext('2d');
  return _scratchCtx;
}

/**
 * Mirror the SVG letterSpacing attribute in canvas measurement so wrap points
 * and alignment use the same advances the preview actually renders with.
 * (The server, in turn, adds letterSpacing per character when drawing.)
 */
function applyMeasureLetterSpacing(ctx: CanvasRenderingContext2D, letterSpacing: string): void {
  if (letterSpacing === 'normal') return;
  const ctxWithSpacing = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in ctxWithSpacing) {
    ctxWithSpacing.letterSpacing = letterSpacing;
  }
}

function baselinesFromTopPx(
  lineCount: number,
  fontSizePt: number,
  boxHeightPt: number,
  layoutMode: FieldTextLayoutMode,
  lineSpacing: number,
  metricsFamily: string,
  verticalOffsetFactor: number,
  displayScale: number
): number[] {
  if (lineCount <= 0) return [];
  if (layoutMode === 'form-line') {
    const bottomBaseline = getTextBaselineFromTop(1, fontSizePt, boxHeightPt, {
      lineSpacing,
      fontFamily: metricsFamily,
      layoutMode,
      verticalOffsetFactor,
    }, displayScale);
    if (lineCount === 1) return [bottomBaseline];
    const step = fontSizePt * lineSpacing * displayScale;
    return Array.from({ length: lineCount }, (_, i) => bottomBaseline - (lineCount - 1 - i) * step);
  }
  const first = getTextBaselineFromTop(lineCount, fontSizePt, boxHeightPt, {
    lineSpacing,
    fontFamily: metricsFamily,
    layoutMode,
    verticalOffsetFactor,
  }, displayScale);
  const step = fontSizePt * lineSpacing * displayScale;
  return Array.from({ length: lineCount }, (_, i) => first + i * step);
}

export interface FieldTextPreviewProps {
  field: SigningField;
  boxWidthPx: number;
  boxHeightPx: number;
  /** Font + wrap scale — must match boxWidthPx (overlay scaleX). */
  horizontalScale: number;
  /** Baseline scale — must match boxHeightPx (overlay scaleY). */
  verticalScale: number;
  placeholder?: string;
}

/** Gray used for placeholder text — always fixed so empty fields are discoverable. */
const PLACEHOLDER_GRAY = '#94a3b8';

export const FieldTextPreview: React.FC<FieldTextPreviewProps> = ({
  field,
  boxWidthPx,
  boxHeightPx,
  horizontalScale,
  verticalScale,
  placeholder,
}) => {
  // BUG-02: distinguish real value from placeholder so we can style them differently.
  const isPlaceholder = !field.value && !!placeholder;
  const textValue = field.value || placeholder || '';
  const layoutMode = fieldTextLayoutMode(field.type, {
    boxHeight: field.height,
    value: field.value,
  });
  const style = resolveFieldTextStyle(field, layoutMode);
  const css = toPreviewCss(style, horizontalScale);
  const verticalOffsetFactor = field.type === 'signature-type' ? 0.78 : 0.82;
  const padX = 4 * horizontalScale;
  const maxWidth = Math.max(8, boxWidthPx - padX * 2);

  const fontSpec = `${css.fontStyle} ${css.fontWeight} ${css.fontSize} ${css.fontFamily}`;

  const isStructuredCompact =
    field.type === 'name' ||
    field.type === 'date' ||
    field.type === 'initials' ||
    field.type === 'signature-type';

  const lines = useMemo(() => {
    if (isStructuredCompact) return [textValue];
    if (typeof document === 'undefined') return [textValue];
    // Reuse the module-scope scratch context rather than allocating a fresh
    // canvas on every memo invocation (this memo reruns on width/font/spacing
    // changes during drag/resize loops).
    const ctx = getScratchCtx();
    if (!ctx) return [textValue];
    ctx.font = fontSpec;
    applyMeasureLetterSpacing(ctx, css.letterSpacing);
    return wrapTextToWidth(textValue, maxWidth, (line) => ctx.measureText(line).width);
  }, [textValue, maxWidth, fontSpec, css.letterSpacing, isStructuredCompact]);

  const baselines = baselinesFromTopPx(
    lines.length,
    style.fontSize,
    field.height,
    layoutMode,
    style.lineHeight,
    style.metricsFamily,
    verticalOffsetFactor,
    verticalScale
  );

  const textAnchor =
    style.align === 'center' ? 'middle' : style.align === 'right' ? 'end' : 'start';

  // BUG-02: placeholder uses fixed gray fill + slight italic; real value uses field color.
  const fillColor = isPlaceholder ? PLACEHOLDER_GRAY : css.color;
  // Placeholder glyphs are always slightly italic for visual distinction.
  const effectiveFontStyle = isPlaceholder ? 'italic' : css.fontStyle;

  return (
    <svg
      width={boxWidthPx}
      height={boxHeightPx}
      style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }}
      aria-hidden
    >
      {/* BUG-02: faint outline so an empty field box is discoverable even when not selected. */}
      {isPlaceholder && (
        <rect
          x={0.5}
          y={0.5}
          width={Math.max(0, boxWidthPx - 1)}
          height={Math.max(0, boxHeightPx - 1)}
          fill="none"
          stroke={PLACEHOLDER_GRAY}
          strokeWidth={0.75}
          strokeDasharray="3 2"
          rx={2}
        />
      )}
      {style.highlightColor && lines.length > 0 && !isPlaceholder && (() => {
        const minY = Math.min(...baselines) - style.fontSize * verticalScale * 0.85;
        const maxY = Math.max(...baselines) + style.fontSize * verticalScale * 0.15;
        return (
          <rect
            x={padX - 2}
            y={minY}
            width={boxWidthPx - padX * 2 + 4}
            height={maxY - minY}
            fill={style.highlightColor}
            rx={2}
          />
        );
      })()}
      {lines.map((line, i) => {
        let lineWidthPx = line.length * style.fontSize * horizontalScale * 0.5;
        if (typeof document !== 'undefined') {
          // Reuse a single module-scope scratch canvas + 2d context to measure
          // text. Allocating a new canvas per line per render (200 fields × 5
          // lines × 60 fps drags) is the dominant cost in this hot path.
          const ctx = getScratchCtx();
          if (ctx) {
            ctx.font = fontSpec;
            applyMeasureLetterSpacing(ctx, css.letterSpacing);
            lineWidthPx = ctx.measureText(line).width;
          }
        }

        const contentW = boxWidthPx - padX * 2;
        const lineLeft = alignedLineX(lineWidthPx, padX, contentW, style.align, layoutMode);
        const xPos =
          textAnchor === 'middle'
            ? lineLeft + lineWidthPx / 2
            : textAnchor === 'end'
              ? lineLeft + lineWidthPx
              : lineLeft;

        return (
          <text
            key={i}
            x={xPos}
            y={baselines[i] ?? baselines[0]}
            dominantBaseline="alphabetic"
            textAnchor={textAnchor}
            fontFamily={css.fontFamily}
            fontSize={css.fontSize}
            fontWeight={css.fontWeight}
            fontStyle={effectiveFontStyle}
            fill={fillColor}
            textDecoration={!isPlaceholder && css.textDecoration !== 'none' ? css.textDecoration : undefined}
            letterSpacing={css.letterSpacing !== 'normal' ? css.letterSpacing : undefined}
          >
            {line}
          </text>
        );
      })}
    </svg>
  );
};

export function buildPreviewFontString(css: PreviewTextCss): string {
  return `${css.fontStyle} ${css.fontWeight} ${css.fontSize} ${css.fontFamily}`;
}